import { useState } from 'react'
import { sortPeople } from '../../utils/peopleSort'

interface OrgUser {
  id: number
  full_name: string
  email: string
  initials: string
}

interface Props {
  orgUsers: OrgUser[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

export default function MemberPicker({ orgUsers, selectedIds, onChange }: Props) {
  const [search, setSearch] = useState('')

  // Task #613: sort after filtering so search results stay alphabetical.
  const filtered = sortPeople(orgUsers.filter(u =>
    u.full_name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  ))

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(i => i !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  return (
    <div className="border border-stone rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-stone bg-[#F8F9FA]">
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted flex-shrink-0">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="flex-1 text-sm bg-transparent outline-none text-text-primary placeholder-text-muted"
          />
          {selectedIds.length > 0 && (
            <span className="text-sm font-semibold text-text-primary bg-charcoal/5 px-2 py-0.5 rounded-full">
              {selectedIds.length} selected
            </span>
          )}
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-stone">
        {filtered.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-text-muted">
            No users match your search
          </div>
        ) : (
          filtered.map(user => (
            <label key={user.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8F9FA] transition-colors">
              <input
                type="checkbox"
                checked={selectedIds.includes(user.id)}
                onChange={() => toggle(user.id)}
                className="w-4 h-4 rounded border-stone text-pinnacle-red focus:ring-pinnacle-red"
              />
              <div className="w-8 h-8 rounded-full bg-charcoal flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {user.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">{user.full_name}</p>
                <p className="text-sm text-text-secondary truncate">{user.email}</p>
              </div>
            </label>
          ))
        )}
      </div>
    </div>
  )
}
